import { FetchMethod, useCodeerApi } from "../useCodeerApi"
import { CurrentUserSchema, ProfileSchema, UserSchema } from "./types"
import { Response } from "@/types"

export interface UserResponse extends UserSchema {}
export interface CurrentUserResponse extends CurrentUserSchema {}

export interface LoginApiRequest {
  email: string
  password: string
}

export interface SignupApiRequest {
  email: string
  password: string
  name?: string
  invitation_token?: string
}

export interface SignupVerifyApiRequest {
  uid: string
  token: string
  invitation_token?: string
}

export interface PasswordResetRequestApiRequest {
  email: string
}

export interface PasswordResetStatusApiRequest {
  uid: string
  token: string
}

export interface PasswordResetConfirmApiRequest {
  uid: string
  token: string
  new_password: string
}

export interface PasswordChangeApiRequest {
  current_password: string
  new_password: string
}

export interface PasswordSetApiRequest {
  new_password: string
}

export interface InvitationStatusResponse {
  email: string
  organization_name: string
  workspace_name: string
  existing_user: boolean
  existing_user_has_password: boolean
}

export interface InvitationAcceptPasswordApiRequest {
  email: string
  password: string
  name?: string
}

export {
  transToUser as userResponseToUser,
  transToCurrentUser as currentUserResponseToUser,
  transToProfile as profileResponseToProfile,
} from "./types"

export const useGetMeApi = () => {
  return useCodeerApi<Response<CurrentUserResponse>>(
    "/accounts/me",
    FetchMethod.Get
  )
}

export const useSearchUserApi = () => {
  return useCodeerApi<Response<UserResponse[]>>(
    "/accounts/search",
    FetchMethod.Get
  )
}

export const useLoginApi = () => {
  return useCodeerApi<Response<CurrentUserResponse>, LoginApiRequest>(
    "/accounts/login",
    FetchMethod.Post
  )
}

export const useSignupApi = () => {
  return useCodeerApi<Response<unknown>, SignupApiRequest>(
    "/accounts/signup",
    FetchMethod.Post
  )
}

export const useSignupVerifyApi = () => {
  return useCodeerApi<Response<CurrentUserResponse>, SignupVerifyApiRequest>(
    "/accounts/signup/verify",
    FetchMethod.Post
  )
}

export const usePasswordResetRequestApi = () => {
  return useCodeerApi<Response<unknown>, PasswordResetRequestApiRequest>(
    "/accounts/password/reset/request",
    FetchMethod.Post
  )
}

export const usePasswordResetStatusApi = () => {
  return useCodeerApi<Response<unknown>, PasswordResetStatusApiRequest>(
    "/accounts/password/reset/status",
    FetchMethod.Post
  )
}

export const usePasswordResetConfirmApi = () => {
  return useCodeerApi<Response<unknown>, PasswordResetConfirmApiRequest>(
    "/accounts/password/reset/confirm",
    FetchMethod.Post
  )
}

export const usePasswordChangeApi = () => {
  return useCodeerApi<Response<unknown>, PasswordChangeApiRequest>(
    "/accounts/me/password/change",
    FetchMethod.Post
  )
}

export const usePasswordSetApi = () => {
  return useCodeerApi<Response<CurrentUserResponse>, PasswordSetApiRequest>(
    "/accounts/me/password/set",
    FetchMethod.Post
  )
}

export const useInvitationStatusApi = (token: string) => {
  return useCodeerApi<Response<InvitationStatusResponse>>(
    `/accounts/invitations/${token}/status`,
    FetchMethod.Get
  )
}

export const useInvitationAcceptPasswordApi = (token: string) => {
  return useCodeerApi<
    Response<CurrentUserResponse>,
    InvitationAcceptPasswordApiRequest
  >(`/accounts/invitations/${token}/accept-password`, FetchMethod.Post)
}

export const useLogoutApi = () => {
  return useCodeerApi<Response<unknown>>("/accounts/logout", FetchMethod.Post)
}

export interface UpdateUserRequest {
  new_name?: string
}

export const useUpdateUserApi = () => {
  return useCodeerApi<Response<CurrentUserResponse>, UpdateUserRequest>(
    "/accounts/me",
    FetchMethod.Put
  )
}

export interface UpdateAvatarRequest {
  avatar: File
}

export const useUpdateAvatarApi = () => {
  return useCodeerApi<Response<CurrentUserResponse>, UpdateAvatarRequest>(
    "/accounts/me/avatar",
    FetchMethod.Post
  )
}

export const useGetProfileApi = () => {
  return useCodeerApi<Response<ProfileSchema>>(
    "/accounts/me/profile",
    FetchMethod.Get
  )
}

export interface UpdateProfileRequest {
  default_workspace_id?: string
  default_organization_id?: string
  default_search?: boolean
  preferred_llm?: string
}

export const useUpdateProfileApi = () => {
  return useCodeerApi<Response<ProfileSchema>, UpdateProfileRequest>(
    "/accounts/me/profile",
    FetchMethod.Put
  )
}
